<script setup lang="ts">
  import { z } from 'zod';

  import { MpButton, MpInput, MpCard, MpText, useMpScreen } from '@borg/ui';
  import { emailValidator } from '@borg/validation';

  const LS_KEY = 'EXTERNAL_APPLICATION_INFO';
  let intentId: number | undefined;

  definePageMeta({
    showHeader: false,
    showFooter: false,
  });

  const { t } = useI18n();
  const { breakpoints } = useMpScreen();
  const route = useRoute();
  const config = useRuntimeConfig();
  const images = useImages();

  const backgroundImage = images['login']
    ? `url('${config.public.assetsBaseUrl}/brand/${config.public.project.name}/login.svg')`
    : 'none';
  const jobId = route.params.jobId?.toString();
  const jobSlug = route.params.slug?.toString();
  const jobApplicationSource = route.query.source?.toString();
  const jobApplicationType: 'external' | 'direct_link' =
    route.query.applicationType === 'external' || route.query.applicationType === 'direct_link'
      ? route.query.applicationType
      : 'external';

  const { state, validate, values } = useForm({
    initialData: () => ({
      email: '',
    }),
    schema: z.object({
      email: emailValidator,
    }),
  });

  async function logExternalInitiatedIntent() {
    if (jobId) {
      const result = await applicationIntentsService.create({
        jobId,
        applicationType: jobApplicationType,
        status: 'initiated',
        source: jobApplicationSource,
      });

      if (result.isSuccess) {
        intentId = result.data.id;
      }
    }
  }

  onBeforeMount(logExternalInitiatedIntent);

  onMounted(() => {
    if (process.client) {
      const previousData = localStorage.getItem(LS_KEY);
      const { email } = previousData ? JSON.parse(previousData) : { email: '' };

      values.email = email;
    }
  });

  async function onSubmit() {
    if (await validate()) {
      const { email } = values;
      try {
        localStorage.setItem(
          LS_KEY,
          JSON.stringify({
            email,
          }),
        );
      } catch (e) {
        logger.error(`Error trying to save user external application data to LS: ${e}`);
      }

      if (intentId) {
        await applicationIntentsService.update(intentId, {
          jobId,
          applicationType: jobApplicationType,
          status: 'initiated',
          source: jobApplicationSource,
          email,
        });
      }

      navigate(
        getExternalJobApplicationLocation(jobId, jobSlug, { intentId: intentId?.toString() }),
      );
    }
  }
</script>

<template>
  <div class="container">
    <MpCard
      class="container__form"
      :p="!breakpoints.isMd ? 'sm' : 'lg'"
      :border="breakpoints.isMd"
      :shadow="breakpoints.isMd"
    >
      <div>
        <LinksLocale to="index">
          <Logo height="40" />
        </LinksLocale>
        <MpText
          class="container__prompt"
          as="h3"
        >
          {{ t('JOB_APPLICATION_EXTERNAL_FORM.PROMPT') }}
        </MpText>
        <form
          class="form"
          @submit.prevent="onSubmit"
        >
          <MpInput
            v-model="values.email"
            required
            :label="$t('SIGN_UP_PAGE.LOGIN_EMAIL_LABEL')"
            :placeholder="$t('SIGN_UP_PAGE.LOGIN_EMAIL_PLACEHOLDER')"
            :error="state.email.message ? t(state.email.message) : undefined"
            size="lg"
            autocomplete="username"
            test="email"
          />

          <AuthPartsDisclaimer :text="$t('SIGN_UP_PAGE.DISCLAIMER.PREFIX_LOGIN')" />
          <MpButton
            type="submit"
            size="lg"
            :text="$t('SIGN_UP_PAGE.LOGIN_BUTTON_LABEL')"
          />
          <MpButton
            variant="secondary"
            size="lg"
            :text="$t('CANCEL')"
            @click="
              () => {
                navigateToJob(
                  jobId,
                  jobSlug,
                  { source: 'external_form_page_cancel_button' },
                  { replace: true },
                );
              }
            "
          />
        </form>
      </div>
    </MpCard>
  </div>
</template>

<style scoped lang="scss">
  $positionRight: top 142px right calc(50% - 360px);
  $fullSize: 360px;

  .container::before {
    content: '';
    position: fixed;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    background-image: v-bind(backgroundImage);
    background-position: $positionRight;
    background-size: $fullSize;
  }

  .container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--mp-space-60);
    overflow: auto;
    position: relative;

    @include screen-md {
      margin-top: var(--mp-space-100);
    }

    &__prompt {
      margin: var(--mp-space-60) 0;
    }

    &__form {
      background-color: rgba(255, 255, 255, 0.7);

      backdrop-filter: blur(25px);
      width: 100%;
      max-width: 544px;

      @include dark-mode {
        background-color: rgba(30, 30, 30, 0.7);
      }
    }
  }

  .form {
    display: flex;
    flex-direction: column;
    gap: var(--mp-space-50);
  }

  .wide {
    display: flex;
    justify-content: flex-end;
  }
</style>
